import { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  InputAdornment,
  IconButton,
  Link,
} from '@mui/material';
import { Visibility, VisibilityOff, Google as GoogleIcon } from '@mui/icons-material';
import { useAuth } from '../../../app/providers/AuthProvider';

type SignInFormProps = {
  readonly onSwitchToSignUp: () => void;
  readonly onSwitchToReset: () => void;
  readonly onClose: () => void;
};

const SignInForm = ({ onSwitchToSignUp, onSwitchToReset, onClose }: SignInFormProps) => {
  const { signIn, signInWithEmail } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleEmailSignIn = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      await signInWithEmail({ email, password });
      onClose();
    } catch (err) {
      const error = err as { code?: string; message: string };

      // Handle specific error codes
      switch (error.code) {
        case 'auth/invalid-credential':
        case 'auth/wrong-password':
        case 'auth/user-not-found':
          setError('Invalid email or password');
          break;
        case 'auth/too-many-requests':
          setError('Too many failed attempts. Please try again later.');
          break;
        case 'auth/user-disabled':
          setError('This account has been disabled');
          break;
        default:
          setError(error.message || 'Failed to sign in');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setError(null);
    setLoading(true);

    try {
      await signIn();
      onClose();
    } catch (err) {
      const error = err as { message: string };
      setError(error.message || 'Failed to sign in with Google');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleEmailSignIn} noValidate>
      <Typography variant="h5" gutterBottom fontWeight="bold">
        Sign In
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Welcome back! Sign in to continue
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        label="Email"
        type="email"
        value={email}
        onChange={e => setEmail(e.target.value)}
        margin="normal"
        required
        autoComplete="email"
        autoFocus
      />

      <TextField
        fullWidth
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={e => setPassword(e.target.value)}
        margin="normal"
        required
        autoComplete="current-password"
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowPassword(!showPassword)}
                edge="end"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <Box sx={{ mt: 1, mb: 2, textAlign: 'right' }}>
        <Link
          component="button"
          type="button"
          variant="body2"
          onClick={onSwitchToReset}
          sx={{ cursor: 'pointer' }}
        >
          Forgot password?
        </Link>
      </Box>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={loading || !email || !password}
        sx={{ mb: 2 }}
      >
        {loading ? 'Signing in...' : 'Sign In'}
      </Button>

      <Box sx={{ position: 'relative', my: 2 }}>
        <Box
          sx={{
            borderTop: 1,
            borderColor: 'divider',
            position: 'absolute',
            width: '100%',
            top: '50%',
          }}
        />
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            position: 'relative',
            display: 'inline-block',
            bgcolor: 'background.paper',
            px: 2,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          OR
        </Typography>
      </Box>

      <Button
        fullWidth
        variant="outlined"
        size="large"
        startIcon={<GoogleIcon />}
        onClick={handleGoogleSignIn}
        disabled={loading}
        sx={{ mb: 2 }}
      >
        Continue with Google
      </Button>

      <Typography variant="body2" color="text.secondary" align="center">
        Don't have an account?{' '}
        <Link
          component="button"
          type="button"
          onClick={onSwitchToSignUp}
          sx={{ cursor: 'pointer' }}
        >
          Sign up
        </Link>
      </Typography>
    </Box>
  );
};

export default SignInForm;
